import { useEffect, useRef, useState } from 'react';
import tailwindConfig from 'tailwindcss/defaultTheme';

const defaultBreakpoints = tailwindConfig.screens;

export const useBreakpoints = <T extends keyof typeof defaultBreakpoints>(
  breakpoints: T[]
) => {
  const breakpointsRef = useRef(breakpoints);

  const [matches, setMatches] = useState<Record<T, boolean>>(() =>
    breakpoints.reduce(
      (acc, breakpoint) => ({
        ...acc,
        [breakpoint]: !!buildQuery(breakpoint).matches,
      }),
      {} as Record<T, boolean>
    )
  );

  useEffect(() => {
    const localBreakpoints = breakpointsRef.current;
    const handlers = new Map();

    localBreakpoints.forEach((breakpoint) => {
      const mediaQueryList = buildQuery(breakpoint);
      const documentChangeHandler = () => {
        setMatches((prev) => ({
          ...prev,
          [breakpoint]: !!mediaQueryList.matches,
        }));
      };

      handlers.set(breakpoint, documentChangeHandler);

      try {
        mediaQueryList.addEventListener('change', documentChangeHandler);
      } catch {
        mediaQueryList.addListener(documentChangeHandler);
      }

      documentChangeHandler();
    });

    return () => {
      localBreakpoints.forEach((breakpoint) => {
        const mediaQueryList = buildQuery(breakpoint);
        const handler = handlers.get(breakpoint);
        try {
          mediaQueryList.removeEventListener('change', handler);
        } catch {
          mediaQueryList.removeListener(handler);
        }
      });
    };
  }, []);

  return matches;
};

function buildQuery(breakpoint: keyof typeof defaultBreakpoints) {
  return window.matchMedia(`(min-width: ${defaultBreakpoints[breakpoint]})`);
}
